import axios from 'axios';
import { getAuthHeader } from './auth';
import { API_BASE_URL } from '../config/api';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'superadmin' | 'admin' | 'operator' | 'staff';
  permissions: string[];
  profile?: {
    title?: string;
    department?: string;
    licenseNumber?: string;
    specialties?: string[];
    phoneNumber?: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  externalId?: string;
  lastEhrSync?: Date;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  department?: string;
  specialty?: string;
  isActive?: boolean;
}

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'operator' | 'staff';
  profile?: {
    title?: string;
    department?: string;
    licenseNumber?: string;
    specialties?: string[];
    phoneNumber?: string;
  };
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  role?: 'superadmin' | 'admin' | 'operator' | 'staff';
  permissions?: string[];
  profile?: {
    title?: string;
    department?: string;
    licenseNumber?: string;
    specialties?: string[];
    phoneNumber?: string;
  };
  isActive?: boolean;
  externalId?: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  expiresAt: string;
  createdAt: string;
  invitedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  metadata?: {
    firstName?: string;
    lastName?: string;
    department?: string;
  };
}

export interface InviteUserData {
  email: string;
  role: 'admin' | 'provider' | 'staff' | 'viewer';
  permissions?: string[];
  metadata?: {
    firstName?: string;
    lastName?: string;
    department?: string;
    inviteMessage?: string;
  };
}

export interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
}

class UserService {
  private getHeaders(): Record<string, string> {
    const authHeader = getAuthHeader();
    return {
      'Content-Type': 'application/json',
      ...(authHeader.Authorization ? { Authorization: authHeader.Authorization } : {})
    };
  }

  async getUsers(filters: UserFilters = {}): Promise<UsersResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await axios.get(
        `${API_BASE_URL}/v1/auth/users?${params.toString()}`,
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch users');
    }
  }

  async getUserById(userId: string): Promise<User> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/v1/auth/users/${userId}`,
        { headers: this.getHeaders() }
      );

      return response.data.user;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch user');
    }
  }

  async updateUser(userId: string, userData: UpdateUserData): Promise<User> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/v1/auth/users/${userId}`,
        userData,
        { headers: this.getHeaders() }
      );

      return response.data.user;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update user');
    }
  }

  async deactivateUser(userId: string): Promise<void> {
    try {
      await axios.put(
        `${API_BASE_URL}/v1/auth/users/${userId}`,
        { isActive: false },
        { headers: this.getHeaders() }
      );
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to deactivate user');
    }
  }

  async activateUser(userId: string): Promise<void> {
    try {
      await axios.put(
        `${API_BASE_URL}/v1/auth/users/${userId}`,
        { isActive: true },
        { headers: this.getHeaders() }
      );
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to activate user');
    }
  }


  async getDepartments(): Promise<string[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/v1/providers/metadata/departments`,
        { headers: this.getHeaders() }
      );

      return response.data.data || [];
    } catch (error: any) {
      return [];
    }
  }

  async getSpecialties(): Promise<string[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/v1/providers/metadata/specialties`,
        { headers: this.getHeaders() }
      );

      return response.data.data || [];
    } catch (error: any) {
      return [];
    }
  }
}

export const userService = new UserService();